import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';

const AdminContentModeration = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, resolved, dismissed
  const [sortBy, setSortBy] = useState('created_at'); // created_at, priority, category
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [durationHours, setDurationHours] = useState(24);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    dismissed: 0
  });

  // Content preview modal state
  const [showContentModal, setShowContentModal] = useState(false);
  const [contentDetails, setContentDetails] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  const getValidToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return null;
    }
    return token;
  };

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [filter, sortBy, sortOrder]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;

      const params = new URLSearchParams({
        status: filter === 'all' ? '' : filter,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      const response = await axios.get(`${BACKEND_URL}/api/admin/content-reports?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError('Failed to load content reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/content-reports/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleTakeAction = async () => {
    if (!selectedReport || !actionType) return;

    setActionLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;

      const actionData = {
        report_id: selectedReport.id,
        action_type: actionType,
        reason: actionReason,
        duration_hours: actionType.includes('restrict') ? durationHours : undefined
      };

      await axios.post(`${BACKEND_URL}/api/admin/moderation-action`, actionData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setShowActionModal(false);
      setSelectedReport(null);
      setActionType('');
      setActionReason('');
      await fetchReports();
      await fetchStats();
    } catch (error) {
      console.error('Error taking action:', error);
      setError('Failed to take moderation action');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchContentDetails = async (reportId) => {
    setContentLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/content-reports/${reportId}/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setContentDetails(response.data);
      setShowContentModal(true);
    } catch (error) {
      console.error('Error fetching content details:', error);
      setError('Failed to load content details');
    } finally {
      setContentLoading(false);
    }
  };

  const handleDismissReport = async (reportId, reason = '') => {
    try {
      const token = getValidToken();
      if (!token) return;

      await axios.put(`${BACKEND_URL}/api/admin/content-reports/${reportId}`, {
        status: 'dismissed',
        resolution_notes: reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchReports();
      await fetchStats();
    } catch (error) {
      console.error('Error dismissing report:', error);
      setError('Failed to dismiss report');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      harassment: '🚫',
      spam: '🗑️',
      hate_speech: '⚠️',
      inappropriate_content: '🔞',
      misinformation: '❌',
      privacy_violation: '🔒',
      impersonation: '👤',
      other: '❓'
    };
    return icons[category] || '❓';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.ALUMNI_MANAGEMENT}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Content Moderation
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Review and moderate reported content across the alumni network.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              ← Back to Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Reports
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.pending}
            </div>
            <div className="text-sm text-yellow-600 dark:text-yellow-400">
              Pending Review
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.resolved}
            </div>
            <div className="text-sm text-green-600 dark:text-green-400">
              Resolved
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {stats.dismissed}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Dismissed
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status Filter
                </label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Reports</option>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="created_at">Date Created</option>
                  <option value="priority">Priority</option>
                  <option value="category">Category</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Order
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
            
            <button
              onClick={fetchReports}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                No reports found for the selected filters.
              </p>
            </div>
          ) : (
            reports.map(report => (
              <div key={report.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{getCategoryIcon(report.category)}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {report.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Report
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Reported {report.content_type} • ID: {report.content_id}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                        {report.priority} priority
                      </span>
                    </div>
                    
                    {report.description && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        <strong>Details:</strong> {report.description}
                      </p>
                    )}
                    
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      <div>Reported by: {report.reporting_user_name} on {formatDate(report.created_at)}</div>
                      <div>Reported user: {report.reported_user_name}</div>
                      {report.resolved_by && (
                        <div>Resolved by: {report.resolved_by_name} on {formatDate(report.resolved_at)}</div>
                      )}
                    </div>
                    
                    {report.resolution_notes && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                        <strong>Resolution Notes:</strong> {report.resolution_notes}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {report.status === 'pending' && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedReport(report);
                            setShowActionModal(true);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          Take Action
                        </button>
                        <button
                          onClick={() => handleDismissReport(report.id, 'Report dismissed after review')}
                          className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => fetchContentDetails(report.id)}
                      disabled={contentLoading}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                      {contentLoading ? 'Loading...' : 'View Content'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Action Modal */}
        {showActionModal && selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Take Moderation Action
                  </h3>
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Action Type
                  </label>
                  <select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select an action...</option>
                    <option value="content_hidden">Hide Content</option>
                    <option value="user_warned">Warn User</option>
                    <option value="posting_restricted">Restrict Posting</option>
                    <option value="commenting_restricted">Restrict Commenting</option>
                    <option value="messaging_restricted">Restrict Messaging</option>
                    <option value="user_suspended">Suspend User</option>
                  </select>
                </div>

                {actionType.includes('restrict') && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration (hours)
                    </label>
                    <input
                      type="number"
                      value={durationHours}
                      onChange={(e) => setDurationHours(parseInt(e.target.value))}
                      min="1"
                      max="8760"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason (Required)
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Explain the reason for this action..."
                    rows={3}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTakeAction}
                    disabled={actionLoading || !actionType || !actionReason.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Processing...' : 'Take Action'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Preview Modal */}
        {showContentModal && contentDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Reported {contentDetails.content_type === 'post' ? 'Post' :
                            contentDetails.content_type === 'comment' ? 'Comment' :
                            contentDetails.content_type === 'message' ? 'Message' : 'Profile'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowContentModal(false);
                      setContentDetails(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Author Info */}
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Author: {contentDetails.author}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Created: {contentDetails.created_at ? new Date(contentDetails.created_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      ID: {contentDetails.content_id}
                    </div>
                  </div>
                </div>

                {/* Content Display */}
                <div className="space-y-4">
                  {contentDetails.content_type === 'post' && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Post Content:</h4>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {contentDetails.content}
                      </p>
                      {contentDetails.image_url && (
                        <div className="mt-3">
                          {/* Check if it's a video file */}
                          {contentDetails.image_url.match(/\.(mp4|webm|ogg|mov|avi)$/i) ? (
                            <video 
                              src={contentDetails.image_url} 
                              controls
                              className="max-w-md rounded-lg"
                              style={{ maxHeight: '400px' }}
                            >
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img 
                              src={contentDetails.image_url} 
                              alt="Post media" 
                              className="max-w-md rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          )}
                          <div style={{ display: 'none' }} className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Media file: <a href={contentDetails.image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {contentDetails.image_url.split('/').pop()}
                              </a>
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="mt-3 flex gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>👍 {contentDetails.likes_count || 0} likes</span>
                        <span>💬 {contentDetails.comments_count || 0} comments</span>
                      </div>
                    </div>
                  )}

                  {contentDetails.content_type === 'comment' && (
                    <div className="space-y-3">
                      {contentDetails.parent_post_content && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Parent Post:</h4>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {contentDetails.parent_post_content}
                          </p>
                        </div>
                      )}
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Comment Content:</h4>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {contentDetails.content}
                        </p>
                      </div>
                    </div>
                  )}

                  {contentDetails.content_type === 'message' && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">💬</span>
                        <h4 className="font-medium text-gray-900 dark:text-white">Chat Message:</h4>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {contentDetails.content}
                        </p>
                      </div>
                      {contentDetails.channel_name && (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Channel: {contentDetails.channel_name}
                        </p>
                      )}
                    </div>
                  )}

                  {(contentDetails.content_type === 'user' || contentDetails.content_type === 'profile') && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Profile Information:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p><span className="font-medium">Name:</span> {contentDetails.content.first_name} {contentDetails.content.last_name}</p>
                          <p><span className="font-medium">Position:</span> {contentDetails.content.current_position || 'Not specified'}</p>
                          <p><span className="font-medium">Company:</span> {contentDetails.content.current_employer || 'Not specified'}</p>
                          <p><span className="font-medium">Location:</span> {contentDetails.content.location || 'Not specified'}</p>
                        </div>
                        <div>
                          {contentDetails.content.headshot_url && (
                            <img 
                              src={contentDetails.content.headshot_url} 
                              alt="Profile photo" 
                              className="h-20 w-20 rounded-full object-cover"
                            />
                          )}
                        </div>
                      </div>
                      {contentDetails.content.bio && (
                        <div className="mt-3">
                          <p className="font-medium">Bio:</p>
                          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {contentDetails.content.bio}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowContentModal(false);
                      setContentDetails(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      // Find the report for this content and trigger the action modal
                      const report = reports.find(r => r.id === contentDetails.report_id);
                      if (report) {
                        setSelectedReport(report);
                        setShowActionModal(true);
                        setShowContentModal(false);
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Take Action
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminRouteGuard>
  );
};

export default AdminContentModeration;